<template>
  <tr :class="{
    'is-package-element': !!serviceData.service_package_id,
    'is-first-package-element': isFirstInPackage,
    'is-last-package-element': isLastInPackage
  }">
    <td class="align-top is-v-select-wrapper p-2">
      <d-badge
        :theme="serviceData.exclude_in_invoice ? 'danger' : 'light'"
        class="pb-0 pt-0 pl-2 pr-0 ml-0 checkbox-wrapper">
        <d-checkbox
          inline
          v-model="serviceData.exclude_in_invoice"
          :disabled="readOnly"
          class="ml-0 mb-0">
          {{ $t('order.excludeInInvoice') }}
        </d-checkbox>
      </d-badge>

      <validation-provider
        v-if="!readOnly"
        rules="required"
        :name="'global-service-id-' + serviceIndex"
        v-slot="{ errors }"
        tag="div">
        <div
          v-if="(!serviceData.service_package_id || (serviceData.service_package_id && serviceData.serviceData && serviceData.serviceData.uses_embroidery)) && (!serviceData.id || serviceInDB)"
          :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
          <v-select
            class="w-100"
            :options="serviceData.service_package_id ? filteredServices : services"
            v-model="serviceData.id"
            @input="updateServicePrices"
            :reduce="service => service.id"
            :placeholder="$t('service.selectService')"
            label="code"
            :filterBy="filterByForServices">
            <template slot="no-options">
              {{ $t('service.noServicesMatchingPhrase') }}&hellip;
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-center w-100 normal-white-space">
                <template v-if="option.group_name">{{ option.group_name }} - </template>{{ option.name }}
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center w-100 normal-white-space">
                <template v-if="option.group_name">{{ option.group_name }} - </template>{{ option.name }}
              </div>
            </template>
          </v-select>
        </div>
        <div v-else>
          {{ serviceData.name }}
          <a
            v-if="!serviceData.service_package_id"
            href="#"
            @click.prevent="serviceData.id = null;">
            <i class="material-icons">edit</i>
            {{ $t('ui.edit') }}
          </a>
        </div>
      </validation-provider>

      <div
        v-if="!readOnly"
        :class="{ 'mt-2': true, 'mb-2': true, 'form-control': true, 'is-select': true }">
        <v-select
          :options="contractors"
          v-model="serviceData.contractor_id"
          :placeholder="$t('contractor.chooseContractor')"
          :reduce="contractor => contractor.id"
          :searchable="false"
          label="name">
        </v-select>
      </div>

      <div
        v-if="readOnly"
        class="pt-2 pb-3">
        {{ serviceData.name }}
        <small v-if="serviceData.contractor_name">
          <br>{{ $t('contractor.contractor') }}: {{ serviceData.contractor_name }}
        </small>
        <template v-if="serviceData.hasDescription">
          <br><strong>{{ $t('ui.description') }}:</strong><br>
          {{ serviceData.description }}
        </template>
        <template v-if="serviceData.hasCustomerDescription">
          <br><strong>{{ $t('service.descriptionFoCustomer') }}:</strong><br>
          <pre style="font-family: sans-serif;">{{ serviceData.description_for_customer }}</pre>
        </template>
      </div>

      <template v-if="!readOnly">
        <a
          v-if="!serviceData.hasDescription && serviceData.id"
          @click.prevent="showServiceDescriptionField"
          href="#add-desc"
          class="mr-3">
          <small>
            <i class="material-icons">add</i> {{ $t('ui.addDescription') }}
          </small>
        </a>

        <div v-if="serviceData.hasDescription">
          <label
            v-if="!readOnly"
            class="mb-0 mt-1">
            <small>{{ $t('service.serviceDescription') }}:</small>
          </label>
          <d-form-textarea
            :placeholder="$t('service.enterAdditionalServiceDescription')"
            :rows="3"
            :max-rows="6"
            :value.prop="serviceData.description"
            class="mt-1"
            v-model="serviceData.description"></d-form-textarea>
        </div>

        <a
          v-if="!serviceData.hasCustomerDescription && serviceData.id"
          @click.prevent="showServiceCustomerDescriptionField"
          href="#add-desc-for-customer"
          class="mr-3">
          <small>
            <i class="material-icons">add</i> {{ $t('service.addDescriptionForCustomer') }}
          </small>
        </a>

        <div v-if="serviceData.hasCustomerDescription">
          <label
            v-if="!readOnly"
            class="mb-0 mt-1">
            <small>{{ $t('service.serviceDescriptionForCustomer') }}:</small>
          </label>

          <d-form-textarea
            :placeholder="$t('service.enterAdditionalServiceDescriptionForCustomer')"
            :rows="3"
            :max-rows="6"
            :value.prop="serviceData.description_for_customer"
            class="mt-1"
            v-model="serviceData.description_for_customer"></d-form-textarea>
        </div>
      </template>

      <order-creator-service-files
        :serviceFiles="serviceData.files"
        :serviceIdInOrder="serviceData.service_id_in_order"
        :readOnly="readOnly" />

      <order-creator-service-embroidery-config
        :embroideryItems="embroideryItems"
        :readOnly="readOnly"
        :serviceData="serviceData" />

      <order-creator-service-samples
        :readOnly="readOnly"
        :isIndent="isIndent"
        :serviceData="serviceData" />

      <table v-if="status && ['NEW', 'CUSTOMER_ORDER', 'SENT_TO_CUSTOMER', 'ORDER'].indexOf(status) === -1 && !datesNotRequired" class="mt-2 mb-2 w-100">
        <tr>
          <td class="align-middle px-1">
            <d-checkbox
              v-if="!userIsWarehouseman"
              inline
              v-model="serviceData.draft_required"
              toggle
              toggle-small
              class="mb-0 mr-0">
              <small>{{ $t('service.sample.requiresDraft') }}</small>
            </d-checkbox>
          </td>
          <td></td>
        </tr>
        <tr v-if="serviceData.draft_required">
          <td class="align-middle px-1">
            <small>
              {{ $t('service.sample.draftDate') }}:
            </small>
          </td>
          <td class="align-middle px-1">
            <d-datepicker
              v-if="!readOnly && serviceData.draft_required"
              v-model="serviceData.draft_at"
              :monday-first="true"
              format="yyyy-MM-dd"
              :placeholder="$t('ui.chooseDate')"
              :language="translationsForDatePicker" />
            <template v-else>
              <template v-if="serviceData.draft_required">
                {{ serviceData.draft_at | formatDate }}
              </template>
            </template>
          </td>
        </tr>
        <tr>
          <td class="align-middle px-0">
            <small v-if="!hasWarning(serviceData.service_id_in_order, 'OBTAINING')">{{ $t('service.sample.sampleReceiveDate') }}:</small>
            <small v-else class="text-danger">{{ $t('service.sample.sampleReceiveDate') }}: <i class="material-icons">warning</i></small>
          </td>
          <td class="align-middle px-0">
            <d-datepicker
              v-if="!readOnly"
              v-model="serviceData.obtaining_at"
              :monday-first="true"
              format="yyyy-MM-dd"
              :placeholder="$t('ui.chooseDate')"
              @input="saveDate('obtaining_at')"
              :language="translationsForDatePicker" />
            <template v-else>
              {{ serviceData.obtaining_at | formatDate }}
            </template>
          </td>
        </tr>
        <tr>
          <td class="align-middle px-0">
            <small v-if="!hasWarning(serviceData.service_id_in_order, 'ACCEPTING')">{{ $t('service.sample.sampleAcceptDate') }}:</small>
            <small v-else class="text-danger">{{ $t('service.sample.sampleAcceptDate') }}: <i class="material-icons">warning</i></small>
          </td>
          <td class="align-middle px-0">
            <d-datepicker
              v-if="(serviceData.obtaining_at || (isIndent && serviceData.accepted_at)) && !readOnly"
              v-model="serviceData.accepted_at"
              :monday-first="true"
              format="yyyy-MM-dd"
              :placeholder="$t('ui.chooseDate')"
              @input="saveDate('accepted_at')"
              :disabled-dates="disabledDatesForAcceptedAt"
              :language="translationsForDatePicker" />
            <template v-else-if="(serviceData.obtaining_at || (isIndent && serviceData.accepted_at)) && readOnly">
              {{ serviceData.accepted_at | formatDate }}
            </template>
          </td>
        </tr>
        <tr>
          <td class="align-middle px-0">
            <small v-if="!hasWarning(serviceData.service_id_in_order, 'ENDING_PRODUCTION')">{{ $t('service.productionCompleteDate') }}:</small>
            <small v-else class="text-danger">{{ $t('service.productionCompleteDate') }}: <i class="material-icons">warning</i></small>
          </td>
          <td class="align-middle px-0">
            <d-datepicker
              v-if="serviceData.accepted_at && !readOnly"
              v-model="serviceData.ended_production_at"
              :monday-first="true"
              format="yyyy-MM-dd"
              :placeholder="$t('ui.chooseDate')"
              @input="saveDate('ended_production_at')"
              :disabled-dates="disabledDatesForEndedProductionAt"
              :language="translationsForDatePicker" />
            <template v-else-if="serviceData.accepted_at && readOnly">
              {{ serviceData.ended_production_at | formatDate }}
            </template>
          </td>
        </tr>
        <tr v-if="!serviceData.ended_production_at">
          <td class="align-middle px-0 pl-1 pr-1">
            <small>{{ $t('service.estimatedCompleteDate') }}:</small>
          </td>
          <td class="align-middle px-0 pl-1 pr-1">
            <small>
              <span
                v-if="serviceData.embroidery_estimated_date"
                class="mr-3">
                {{ serviceData.embroidery_estimated_date }}
              </span>

              <a
                href="#"
                @click.prevent="getEstimatedDate()">
                <template v-if="estimationInProgress">
                  <div
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                </template>
                <template v-else>
                  {{ $t('service.checkEstimatedDate') }}
                </template>
              </a>
            </small>
          </td>
        </tr>
        <tr>
          <td class="align-middle px-0 pl-1 pr-1">
            <small class="text-danger"><strong>{{ $t('ui.deadlineCap') }}:</strong></small>
          </td>
          <td class="align-middle px-0 pl-1 pr-1">
            <small class="text-danger"><strong>{{ formattedDeadline }}</strong></small>
          </td>
        </tr>
      </table>

      <p
        v-else
        class="mt-3 ml-1 mr-1">
        <small>{{ $t('service.estimatedCompleteDate') }}:</small>

        <small>
          <span
            v-if="serviceData.embroidery_estimated_date"
            class="mr-3">
            {{ serviceData.embroidery_estimated_date }}
          </span>

          <a
            href="#"
            @click.prevent="getEstimatedDate()">
            <template v-if="estimationInProgress">
              <div
                class="spinner-border spinner-border-sm"
                role="status"></div>
            </template>
            <template v-else>
              {{ $t('service.checkEstimatedDate') }}
            </template>
          </a>
        </small>
      </p>

      <p
        v-if="serviceData.sample_status"
        class="mb-2">
        <small>
          {{ $t('service.sample.sampleStatus') }}:
          <span
            v-if="serviceData.sample_status === 'APPROVED'"
            class="text-success">
            {{ $t('service.sample.sampleStatuses.approved') }}
          </span>
          <span
            v-if="serviceData.sample_status === 'REJECTED'"
            class="text-danger">
            {{ $t('service.sample.sampleStatuses.rejected') }}
          </span>
          <span
            v-if="serviceData.sample_status === 'WAITING'"
            class="text-primary">
            {{ $t('service.sample.sampleStatuses.waiting') }}
          </span>
        </small>
        <br v-if="serviceData.sample_status === 'APPROVED'">
        <small v-if="serviceData.sample_status === 'APPROVED'">
          {{ $t('service.sample.sampleHplAcceptedAt') }}:
          <span class="text-primary">
            {{ serviceData.sample_hpl_accepted_at }}
          </span>
        </small>
      </p>

      <div v-if="(!serviceData.sample_images || !serviceData.sample_images.length) && serviceData.dropbox_catalog">
        <small>
          <a
            :href="serviceData.dropbox_catalog"
            target="_blank"
            rel="noreferrer noopener">
            <i class="material-icons">catalog</i> {{ $t('service.dropboxCatalog') }}
          </a>
        </small>
      </div>

      <div
        v-if="serviceData.sample_images && serviceData.sample_images.length"
        class="border px-3 py-2 mb-3">
        <strong>
          {{ $t('service.sample.samplePhotosTitle') }}:
        </strong>

        <div v-if="serviceData.dropbox_catalog">
          <small>
            <a
              :href="serviceData.dropbox_catalog"
              target="_blank"
              rel="noreferrer noopener">
              <i class="material-icons">folder</i> {{ $t('service.dropboxCatalog') }}
            </a>
          </small>
        </div>

        <div
          v-for="(sample, index) of serviceData.sample_images"
          :key="'sample-image-' + index + '-' + sample.order_service_id + '-' + sample.id">
          <small>
            {{ $t('service.sample.samplePhotosDateCreated') }}: {{ sample.created_at }}
            <span
              @click.prevent="seeSampleImage(sample.image_preview_url)"
              class="text-primary"
              style="cursor: pointer;">
              <i class="material-icons">visibility</i>
              {{ $t('service.sample.samplePhotosSeePreview') }}
            </span>

            <a
              :href="sample.image_download_url"
              download
              class="ml-2">
              <i class="material-icons">cloud_download</i>
              {{ $t('service.sample.samplePhotosDownload') }}
            </a>
          </small>
        </div>

        <small>
          <a
            :href="serviceData.sample_images_url"
            download>
            <i class="material-icons">cloud_download</i>
            {{ $t('service.sample.samplePhotosDownloadAll') }}
          </a>
        </small>
      </div>

      <d-button
        v-if="!userIsWarehouseman && serviceData.obtaining_at && !userIsCustomer && !serviceData.embriodery_id"
        theme="danger"
        outline
        size="sm"
        class="mr-2"
        @click.prevent="rejectSample()">
        {{ $t('service.sample.rejectSample') }}
      </d-button>

      <d-button
        v-if="!userIsWarehouseman && serviceData.obtaining_at && !userIsCustomer && serviceData.embriodery_id && serviceData.sample_status !== 'APPROVED' && serviceData.sample_status !== 'REJECTED'"
        theme="danger"
        outline
        size="sm"
        class="mr-2 mt-2"
        @click.prevent="rejectEmbrioderySample()">
        {{ $t('service.sample.rejectEmbrioderySample') }}
      </d-button>

      <d-button
        v-if="!userIsWarehouseman && serviceData.obtaining_at && !userIsCustomer && serviceData.embriodery_id && serviceData.sample_status !== 'APPROVED' && serviceData.sample_status !== 'REJECTED'"
        theme="success"
        outline
        size="sm"
        class="mr-2 mt-2"
        @click.prevent="acceptEmbrioderySample()">
        {{ $t('service.sample.acceptEmbrioderySample') }}
      </d-button>

      <d-button
        v-if="!userIsWarehouseman && serviceData.obtaining_at && !userIsCustomer && serviceData.embriodery_id && serviceData.sample_status !== 'APPROVED' && serviceData.sample_status !== 'REJECTED'"
        theme="success"
        size="sm"
        class="mr-2 mt-2"
        @click.prevent="acceptBothEmbrioderySample()">
        {{ $t('service.sample.acceptBothEmbrioderySample') }}
      </d-button>
    </td>
    <td class="align-top pt-2">
      <validation-provider
        v-if="!readOnly && !serviceData.service_package_id"
        rules="required|min_value:1"
        :name="'global-service-amount-' + serviceIndex"
        v-slot="{ errors }">
        <d-input-group>
          <d-input
            :min="1"
            :state="errors.length ? 'invalid' : null"
            :ref="'global-service-amount-' + serviceIndex"
            v-model="serviceData.count"
            @input="servicePricesUpdateDueQtyChange(serviceData.id)"
            type="number" />
          <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
        </d-input-group>
      </validation-provider>
      <template v-else>
        {{ serviceData.count }} {{ $t('ui.piecesAbbr') }}
      </template>

      <small
        v-if="!userIsCustomer && parseInt(serviceData.count, 10) != quantity && !serviceData.service_package_id"
        class="text-danger cursor-pointer d-block"
        @click="setServiceAmountToProductAmount()">
        {{ $t('ui.productsGenitive') }}: {{ quantity }} {{ $t('ui.piecesAbbr') }}
      </small>
    </td>
    <td class="align-top pt-2">
      <validation-provider
        v-if="!readOnly"
        rules="required|min_value:0"
        :name="'global-service-price-' + serviceIndex"
        v-slot="{ errors }">
        <d-input-group :class="{'is-invalid': errors.length}">
          <input
            :class="{
              'form-control': true,
              'form-control-sm': true,
              'is-invalid': errors.length
            }"
            :invalid="errors.length ? true : null"
            type="number"
            :disabled="!serviceData.use_custom_price"
            v-model="serviceData.price" />
          <d-input-group-text slot="append">
            {{ selectedCurrencySymbol }}
          </d-input-group-text>
        </d-input-group>

        <div class="invalid-feedback">
          {{ errors[0] }}
        </div>
      </validation-provider>
      <template v-else>
        {{ Number(serviceData.price).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
      </template>

      <small
        v-if="serviceData.id && !userIsCustomer && !readOnly"
        class="text-primary cursor-pointer"
        @click="setServicePriceFromPricing()">
        {{ $t('price.pricesList') }}: {{ getServicePrice() }} {{ selectedCurrencySymbol }}<br>
      </small>
      <small v-if="serviceData.id">
        {{ $t('price.pricePerItemFrom', { price: serviceData.serviceData.price_multiple_from }) }}
      </small>
      <small
        v-if="!userIsCustomer && serviceData.id && currentServicePriceMultipleFrom !== null && serviceData.serviceData.price_multiple_from !== currentServicePriceMultipleFrom"
        class="text-danger">
        {{ $t('price.differentPriceMultipleFromInfo', { value: currentServicePriceMultipleFrom }) }}
      </small>
    </td>
    <td class="align-top pt-2">
      <validation-provider
        v-if="!readOnly"
        rules="required|min_value:0"
        :name="'global-service-setup-price-' + serviceIndex"
        v-slot="{ errors }">
        <d-input-group :class="{'is-invalid': errors.length}">
          <input
            :class="{
              'form-control': true,
              'form-control-sm': true,
              'is-invalid': errors.length
            }"
            :invalid="errors.length ? true : null"
            type="number"
            :disabled="(!includeSetupPrice && !serviceData.use_custom_price) || !serviceData.use_custom_price"
            v-model="serviceData.setupPrice" />
          <d-input-group-text slot="append">
            {{ selectedCurrencySymbol }}
          </d-input-group-text>
        </d-input-group>

        <div class="invalid-feedback">
          {{ errors[0] }}
        </div>
      </validation-provider>
      <template v-else>
        {{ Number(parseFloat(serviceData.setupPrice)).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
      </template>

      <small
        v-if="includeSetupPrice && !userIsCustomer && !readOnly"
        class="text-primary cursor-pointer"
        @click="setSetupPriceFromPricing()">
        {{ $t('price.pricesList') }}: {{ getServiceSetupPrice() }} {{ selectedCurrencySymbol }}
      </small>
    </td>
    <td class="align-top pt-2">
      <template v-if="serviceData.totalPrice && !Number.isNaN(serviceData.totalPrice)">
        <template v-if="customerServicesDiscount > 0 && !serviceData.use_custom_price">
          <span class="text-primary" style="display: block; line-height: 1;">{{ serviceData.totalDiscountedPrice }} {{ selectedCurrencySymbol }}</span>
          <del style="display: block; line-height: 1;"><small>{{ serviceData.totalPrice }} {{ selectedCurrencySymbol }}</small></del>
        </template>
        <template v-else>
          <span class="text-primary">
            {{ serviceData.totalPrice }} {{ selectedCurrencySymbol }}
          </span>
        </template>
      </template>

      <small
        v-if="customerServicesDiscount > 0 && serviceData.use_custom_price"
        class="text-danger d-block">
        {{ $t('product.notIncludedInDiscounts') }}
      </small>

      <d-checkbox
        inline
        v-model="serviceData.use_custom_price"
        :disabled="readOnly"
        toggle
        toggle-small
        class="ml-0 mb-0 mt-2">
        <small style="display: block; line-height: 1.1;">
          {{ $t('order.useCustomPrice') }}
        </small>
      </d-checkbox>
    </td>
    <td
      v-if="!readOnly"
      class="align-top pt-2">
      <d-button
        v-if="!serviceData.service_package_id"
        size="sm"
        outline
        @click.prevent="removeService"
        theme="danger">
        {{ $t('ui.delete') }}
      </d-button>
    </td>
  </tr>
</template>

<script>
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import FormUtils from './../../utils/FormUtils.js';
import datepickerTranslations from './../../data/datepicker-translations.js';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';
import OrderCreatorServiceEmbroideryConfig from './OrderCreatorServiceEmbroideryConfig.vue';
import OrderCreatorServiceFiles from './OrderCreatorServiceFiles.vue';
import OrderCreatorServiceSamples from './OrderCreatorServiceSamples.vue';

export default {
  name: 'order-creator-service',
  mixins: [
    CustomerPanel
  ],
  props: [
    'contractors',
    'currencyId',
    'readOnly',
    'includeSetupPrice',
    'isIndent',
    'serviceData',
    'serviceIndex',
    'selectedCurrencySymbol',
    'services',
    'customerProductsDiscount',
    'customerServicesDiscount',
    'orderSetUpInProgress',
    'status',
    'quantity',
    'warnings',
    'datesNotRequired',
    'deadline',
    'embroideryItems',
    'isFirstInPackage',
    'isLastInPackage'
  ],
  components: {
    'order-creator-service-embroidery-config': OrderCreatorServiceEmbroideryConfig,
    'order-creator-service-files': OrderCreatorServiceFiles,
    'order-creator-service-samples': OrderCreatorServiceSamples
  },
  computed: {
    serviceInDB () {
      let foundedService = this.services.filter(service => service.id === this.serviceData.id);
      return foundedService && foundedService.length;
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    disabledDatesForAcceptedAt () {
      return {
        to: this.serviceData['obtaining_at']
      };
    },
    disabledDatesForEndedProductionAt () {
      return {
        to: this.serviceData['accepted_at']
      };
    },
    currentServicePriceMultipleFrom () {
      if (!this.services.length) {
        return null;
      }

      let foundedService = this.services.filter(service => service.id === this.serviceData.id);

      if (!(foundedService && foundedService[0])) {
        return null;
      }

      return foundedService[0].price_multiple_from;
    },
    formattedDeadline () {
      if (!this.deadline) {
        return this.$t('ui.lack');
      }

      return [this.deadline.getFullYear(), this.deadline.getMonth() + 1, this.deadline.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    userIsWarehouseman () {
      return this.$store.getters.getUserType === 'warehouse_manager';
    },
    filteredServices () {
      let groupID = 0;

      if (this.serviceData.serviceData && this.serviceData.serviceData.group_id) {
        groupID = this.serviceData.serviceData.group_id;
      }

      return this.services.filter(service => service.group_id === groupID);
    }
  },
  filters: {
    formatDate (input) {
      if (input) {
        return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      }

      return '';
    }
  },
  watch: {
    includeSetupPrice () {
      this.updateServiceSetupPrice(false, 'skip_custom_prices');
    }
  },
  data () {
    return {
      estimationInProgress: false
    };
  },
  mounted () {
    this.updateServiceSetupPrice(false, 'skip_custom_prices');
    this.$bus.$on('order-update-service-prices', this.updateServicePricesCallback);
    this.$bus.$on('order-update-global-service-prices', this.updateServicePricesCallback);
    this.$bus.$on('order-update-service-price', this.updateServicePriceCallback);
    this.$bus.$on('order-update-service-qty-from-package', this.updateServiceQtyAndPrices);
  },
  methods: {
    filterByForServices (option, label, search) {
      return (option.code + ' ' + option.group_name + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    removeService () {
      let serviceID = this.serviceData.service_id_in_order;

      if (this.serviceData.sid) {
        serviceID = this.serviceData.sid;
      }

      this.$parent.$parent.servicesData.splice(this.serviceIndex, 1);

      for (let i = 0; i < this.$parent.$parent.productsData.length; i++) {
        if (this.$parent.$parent.productsData[i].global_services && typeof this.$parent.$parent.productsData[i].global_services[serviceID] !== 'undefined') {
          Vue.delete(this.$parent.$parent.productsData[i].global_services, serviceID);
        }
      }
    },
    updateServicePrices (serviceID) {
      if (this.orderSetUpInProgress) {
        return;
      }

      this.updateServicePrice(serviceID);
      this.updateServiceSetupPrice(serviceID);
    },
    updateServicePricesCallback () {
      if (this.orderSetUpInProgress) {
        return;
      }

      this.updateServicePrice(false);
      this.updateServiceSetupPrice(false);
    },
    updateServicePriceCallback (productIndex, serviceIndex) {
      if (this.orderSetUpInProgress) {
        return;
      }

      if (productIndex === 'global' && serviceIndex === this.serviceIndex) {
        this.updateServicePrice(false);
      }
    },
    // Update when qty changes on input
    servicePricesUpdateDueQtyChange (serviceID) {
      // Skip when order setup in progress
      if (this.orderSetUpInProgress) {
        return;
      }

      // Skip when custom price used
      if (this.serviceData.use_custom_price) {
        return;
      }

      // Skip when no service set
      if (!serviceID) {
        return;
      }

      if (!this.serviceData.count) {
        return;
      }

      this.updateServicePrices(serviceID);
    },
    updateServiceSetupPrice (serviceID, mode = 'normal') {
      if (this.orderSetUpInProgress) {
        return;
      }

      // exclude custom price update when setup price setting is changed or service is cloned
      if (mode === 'skip_custom_prices' && this.serviceData.use_custom_price) {
        return;
      }

      let serviceItem;

      if (serviceID) {
        serviceItem = this.services.filter(service => service.id === serviceID)[0];
        this.$parent.$parent.servicesData[this.serviceIndex].serviceData = JSON.parse(JSON.stringify(serviceItem));
      } else {
        serviceItem = this.serviceData.serviceData;
      }

      if (!serviceItem || !serviceItem.prices || !this.includeSetupPrice) {
        Vue.set(this.$parent.$parent.servicesData[this.serviceIndex], 'setupPrice', '0.00');
        return;
      }

      let serviceSetupPrices = serviceItem.prices.filter(price => price.currency_id === this.currencyId && price.is_setup);

      if (!serviceSetupPrices.length) {
        Vue.set(this.$parent.$parent.servicesData[this.serviceIndex], 'setupPrice', '0.00');
        return;
      }

      serviceSetupPrices.sort((priceA, priceB) => priceA.value_from - priceB.value_from);
      let priceIndexToUse = -1;

      for (let i = 0; i < serviceSetupPrices.length; i++) {
        if (serviceSetupPrices[i].value_from >= this.serviceData.count) {
          priceIndexToUse = i;
          break;
        }
      }

      if (priceIndexToUse === -1) {
        Vue.set(this.$parent.$parent.servicesData[this.serviceIndex], 'setupPrice', '0.00');
        return;
      }

      Vue.set(this.$parent.$parent.servicesData[this.serviceIndex], 'setupPrice', Number(parseFloat(serviceSetupPrices[priceIndexToUse].price)).toFixed(2));
    },
    getServiceSetupPrice () {
      let serviceItem = this.serviceData.serviceData;

      if (!serviceItem || !serviceItem.prices || !this.includeSetupPrice) {
        return '0.00';
      }

      let serviceSetupPrices = serviceItem.prices.filter(price => price.currency_id === this.currencyId && price.is_setup);

      if (!serviceSetupPrices.length) {
        return '0.00';
      }

      serviceSetupPrices.sort((priceA, priceB) => priceA.value_from - priceB.value_from);
      let priceIndexToUse = -1;

      for (let i = 0; i < serviceSetupPrices.length; i++) {
        if (serviceSetupPrices[i].value_from >= this.serviceData.count) {
          priceIndexToUse = i;
          break;
        }
      }

      if (priceIndexToUse === -1) {
        return '0.00';
      }

      return Number(parseFloat(serviceSetupPrices[priceIndexToUse].price)).toFixed(2);
    },
    updateServicePrice (serviceID) {
      if (this.orderSetUpInProgress) {
        return;
      }

      let serviceItem;

      if (serviceID) {
        serviceItem = this.services.filter(service => service.id === serviceID)[0];
        this.$parent.$parent.servicesData[this.serviceIndex].serviceData = JSON.parse(JSON.stringify(serviceItem));
      } else {
        serviceItem = this.serviceData.serviceData;
      }

      if (!serviceItem || !serviceItem.prices) {
        Vue.set(this.$parent.$parent.servicesData[this.serviceIndex], 'price', '0.00');
        return;
      }

      let servicePrices = serviceItem.prices.filter(price => price.currency_id === this.currencyId && !price.is_setup);

      if (!servicePrices.length) {
        Vue.set(this.$parent.$parent.servicesData[this.serviceIndex], 'price', '0.00');
        return;
      }

      servicePrices.sort((priceA, priceB) => priceB.value_from - priceA.value_from);
      let priceIndexToUse = -1;

      for (let i = 0; i < servicePrices.length; i++) {
        if (servicePrices[i].value_from <= this.serviceData.count) {
          priceIndexToUse = i;
          break;
        }
      }

      if (priceIndexToUse === -1) {
        Vue.set(this.$parent.$parent.servicesData[this.serviceIndex], 'price', '0.00');
        return;
      }

      Vue.set(this.$parent.$parent.servicesData[this.serviceIndex], 'price', Number(parseFloat(servicePrices[priceIndexToUse].price)).toFixed(2));
    },
    getServicePrice () {
      let serviceItem = this.serviceData.serviceData;

      if (!serviceItem || !serviceItem.prices) {
        return '0.00';
      }

      let servicePrices = serviceItem.prices.filter(price => price.currency_id === this.currencyId && !price.is_setup);

      if (!servicePrices.length) {
        return '0.00';
      }

      servicePrices.sort((priceA, priceB) => priceB.value_from - priceA.value_from);
      let priceIndexToUse = -1;

      for (let i = 0; i < servicePrices.length; i++) {
        if (servicePrices[i].value_from <= this.serviceData.count) {
          priceIndexToUse = i;
          break;
        }
      }

      if (priceIndexToUse === -1) {
        return '0.00';
      }

      return Number(parseFloat(servicePrices[priceIndexToUse].price)).toFixed(2);
    },
    saveDate (dateType) {
      let dateName = '';
      let preparedDate = [this.serviceData[dateType].getFullYear(), this.serviceData[dateType].getMonth() + 1, this.serviceData[dateType].getDate()].map(n => n < 10 ? '0' + n : n).join('-');

      switch (dateType) {
        case 'obtaining_at': dateName = this.$t('service.sample.sampleReceive'); break;
        case 'accepted_at': dateName = this.$t('service.sample.sampleAccept'); break;
        case 'ended_production_at': dateName = this.$t('service.productionFinish'); break;
        default:
          dateName = '-';
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/services/change-date/' + this.serviceData.service_id_in_order,
        formData: {
          date_row: dateType,
          date: preparedDate
        },
        successTitle: this.$t('service.dateSaved') + ' ' + dateName,
        successText: '',
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.dateChangeErrorText', { value: dateName })
      });
    },
    hasWarning (id, type) {
      if (this.warnings && this.warnings.filter(warning => warning.order_service_id === id && warning.type === type).length) {
        return true;
      }

      return false;
    },
    showServiceDescriptionField () {
      Vue.set(this.serviceData, 'hasDescription', true);
    },
    showServiceCustomerDescriptionField () {
      Vue.set(this.serviceData, 'hasCustomerDescription', true);
    },
    rejectSample () {
      Vue.swal({
        title: this.$t('service.sample.sampleRejectConfirmTitle'),
        text: this.$t('service.sample.sampleRejectConfirmText'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('service.sample.yesReject'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          FormUtils.submit({
            $http: this.$http,
            endpoint: '/orders/services/reject-sample/' + this.serviceData.service_id_in_order,
            successTitle: this.$t('service.sample.sampleRejectSuccessTitle'),
            successText: '',
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('service.sample.sampleRejectErrorText'),
            successAction: () => {
              this.serviceData.obtaining_at = null;
              this.serviceData.accepted_at = null;
              this.serviceData.ended_production_at = null;
            }
          });
        }
      });
    },
    rejectEmbrioderySample () {
      Vue.swal({
        title: this.$t('service.sample.sampleRejectConfirmTitle'),
        text: this.$t('service.sample.sampleRejectConfirmText'),
        type: 'question',
        input: 'text',
        inputPlaceholder: this.$t('service.sample.provideRejectionReason'),
        inputValue: '',
        inputValidator: (value) => {
          if (!value) {
            return this.$t('service.sample.rejectionReasonCannotBeEmpty');
          }

          return false;
        },
        showCancelButton: true,
        confirmButtonText: this.$t('service.sample.yesReject'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          FormUtils.submit({
            $http: this.$http,
            endpoint: '/orders/services/reject-sample/' + this.serviceData.service_id_in_order,
            formData: {
              reject_reason: result.value
            },
            successTitle: this.$t('service.sample.sampleRejectSuccessTitle'),
            successText: '',
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('service.sample.sampleRejectErrorText'),
            successAction: () => {
              this.serviceData.obtaining_at = null;
              this.serviceData.accepted_at = null;
              this.serviceData.ended_production_at = null;
            }
          });
        }
      });
    },
    acceptBothEmbrioderySample () {
      this.acceptEmbrioderySample(true);
    },
    acceptEmbrioderySample (acceptBoth = false) {
      let endpointToUse = '/orders/services/accept-sample/' + this.serviceData.service_id_in_order;

      if (acceptBoth) {
        endpointToUse = '/orders/services/accept-sample-and-customer/' + this.serviceData.service_id_in_order;
      }

      Vue.swal({
        title: this.$t('service.sample.sampleAcceptConfirmTitle'),
        text: this.$t('service.sample.sampleAcceptConfirmText'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('service.sample.yesAccept'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          FormUtils.submit({
            $http: this.$http,
            endpoint: endpointToUse,
            successTitle: this.$t('service.sample.sampleAcceptSuccessTitle'),
            successText: '',
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('service.sample.sampleAcceptErrorText'),
            successAction: () => {
              this.serviceData.accepted_at = new Date();
              this.serviceData.sample_status = 'APPROVED';
            }
          });
        }
      });
    },
    setSetupPriceFromPricing () {
      Vue.set(this.serviceData, 'setupPrice', this.getServiceSetupPrice());
    },
    setServicePriceFromPricing () {
      Vue.set(this.serviceData, 'price', this.getServicePrice());
    },
    setServiceAmountToProductAmount () {
      Vue.set(this.serviceData, 'count', this.quantity);
    },
    seeSampleImage (url) {
      this.$bus.$emit('order-show-sample-preview', url);
    },
    getEstimatedDate () {
      if (this.estimationInProgress) {
        return;
      }

      this.estimationInProgress = true;

      FormUtils.submitInBackground({
        method: 'post',
        $http: this.$http,
        endpoint: '/embroidery/estimate-service-execution',
        formData: {
          service_id: +this.serviceData.id,
          quantity: +this.serviceData.count
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.issueDuringEstimation'),
        successAction: (response) => {
          this.estimationInProgress = false;

          if (!response.data) {
            Vue.swal({
              title: this.$t('service.estimationIssueTitle'),
              text: this.$t('service.estimationIssueText'),
              type: 'warning'
            });
          } else {
            this.serviceData.embroidery_estimated_date = response.data.estimated_date;
            this.serviceData.embroidery_date_tokens.push(response.data.token);
          }
        },
        failAction: () => {
          this.estimationInProgress = false;
        }
      });
    },
    updateServiceQtyAndPrices (packageID, newQty) {
      if (this.orderSetUpInProgress) {
        return;
      }

      if (this.serviceData.service_package_id === packageID) {
        Vue.set(this.serviceData, 'count', newQty);

        Vue.nextTick(() => {
          this.servicePricesUpdateDueQtyChange(this.serviceData.id);
        });
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-update-service-prices', this.updateServicePricesCallback);
    this.$bus.$off('order-update-global-service-prices', this.updateServicePricesCallback);
    this.$bus.$off('order-update-service-price', this.updateServicePriceCallback);
    this.$bus.$off('order-update-service-qty-from-package', this.updateServiceQtyAndPrices);
  }
}
</script>

<style lang="scss" scoped>
.is-v-select-wrapper > div {
  width: 100%;
}

.cursor-pointer {
  cursor: pointer;
}

.normal-white-space {
  line-height: 1.2;
  white-space: normal;
}

.is-package-element > td {
  background: rgba(255, 180, 0, 0.05);
}

.is-package-element > td:first-child {
  border-left: 2px solid rgba(255, 180, 0, 1);
}

.is-package-element > td:last-child {
  border-right: 2px solid rgba(255, 180, 0, 1);
}

.is-first-package-element > td {
  border-top: 2px solid rgba(255, 180, 0, 1);
}

.is-last-package-element > td {
  border-bottom: 2px solid rgba(255, 180, 0, 1);
}
</style>
